import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationExtras, ParamMap, Router } from '@angular/router';
import { CoreService, DialogResult, DialogService, SaveFormEvent, SpinnerService } from 'dspx-core';
import { filter, finalize, switchMap, take } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from '../../constants/api-endpoints.constants';
import { OVERVIEW_CARDSET_LAYOUT_FIELD_TYPE_REPLACEMENTS, POP_UP_CODES, REFERENCE_DATA_IDS, SAVE_RESULT_STATUS } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { CareAllowancePlacementHoldPopupQuestion } from '../../interfaces/care-allowance-placement-hold-popup-question.interface';
import { CareAllowancePlacementReleaseHoldPopupQuestion } from '../../interfaces/care-allowance-placement-release-hold-popup-question.interface';
import { CareAllowancePlacementResponse } from '../../interfaces/care-allowance-placement-response.interface';
import { CareAllowancePlacementHoldRequest } from '../../models/care-allowance-placement-hold-request';
import { CareAllowancePlacementReleaseHoldRequest } from '../../models/care-allowance-placement-release-hold-request';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';
import { CareAllowanceCeaseLocalAdoptionSaveRequest } from 'src/app/interfaces/care-allowance-cease-local-adoption-request.interface';
import { getDateFromDDMMYYYYString } from 'src/app/helpers/date-helper';

@Component({
  selector: 'app-care-allowance-placement',
  templateUrl: './care-allowance-placement.component.html',
  styleUrl: './care-allowance-placement.component.scss'
})
export class CareAllowancePlacementComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {
  fieldTypeReplacements = OVERVIEW_CARDSET_LAYOUT_FIELD_TYPE_REPLACEMENTS;
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormConfig;

  templateId: number = TEMPLATES.careAllowancePlacement;
  versionNo: number = VERSIONS.latest;
  model: any;
  activeDialog: string = '';
  id: string = '';

  private response: CareAllowancePlacementResponse;
  private routePrefix = '';

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    private coreService: CoreService,
    private errorService: ErrorService,
    private spinnerService: SpinnerService
  ) {
    super(router, appDataService, dialogService);
  }

  ngOnInit(): void {
    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const id = paramMap.get('id');
        if (id) {
          this.id = id;
          this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowancePlacementConfigAndResponse;
        }
      })
    );

    this.coreService.conditionFunctions = {
      showOnHoldReason: () => this.isPlacementOnHold(),
      showOnHoldComments: () => this.isPlacementOnHold(),
      showPlacedOnHoldBy: () => this.isPlacementOnHold(),
      showPlacedOnHoldDate: () => this.isPlacementOnHold(),
      showReleaseFromHoldReason: () => this.isPlacementJustReleasedFromHold(),
      showReleasedFromHoldComments: () => this.isPlacementJustReleasedFromHold(),
      showReleasedFromHoldBy: () => this.isPlacementJustReleasedFromHold(),
      showReleasedFromHoldDate: () => this.isPlacementJustReleasedFromHold(),
    };

    this.coreService.applicationFunctions = {
      viewAdjustments: () => this.viewAdjustments(),
      viewInvoices: () => this.viewInvoices(),
      holdPlacement: () => this.holdPlacement(),
      releasePlacementHold: () => this.releasePlacementHold(),
      showViewAdjustmentsButton: () => this.showViewAdjustmentsButton(),
      showHoldPlacementButton: () => this.showHoldPlacementButton(),
      showReleasePlacementHoldButton: () => this.showReleasePlacementHoldButton(),
      showCeasePlacementButton: () => this.showCeasePlacementButton(),
      ceasePlacement: () => this.ceasePlacement(),
      exit: () => this.navigateBackToHome(),
    };

    // We get the first part of the URL so that we can navigate back to that route after the Cancel or Save button is clicked.
    let index = this.router.url.indexOf(`/${ROUTE_CONSTANTS.careAllowancePlacement.prefix}/`);
    this.routePrefix = this.router.url.substring(0, index);
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded(event: any): void {
    this.response = event as CareAllowancePlacementResponse;
  }

  modelChanged(event: any): void {
  }

  filter(event: any): void {
  }

  buttonClicked(event: any): void {
  }

  save(event: SaveFormEvent) {
  }

  cancel(event: any) {
    this.navigateBackToHome();
  }

  dialogButton_holdPlacementPopup_Clicked(event: any) {
    this.completeHoldPlacement(event as CareAllowancePlacementHoldPopupQuestion);
  }

  dialogButton_releasePlacementHoldPopup_Clicked(event: any) {
    this.completeReleasePlacementHold(event as CareAllowancePlacementReleaseHoldPopupQuestion);
  }

  actionButton_applicationGrid_datagrid_colLink_applicationNumber_Clicked(event: any) {
    switch (event.event.data.applicationTypeId) {
      case REFERENCE_DATA_IDS.applicationType.createPlacement:
      case REFERENCE_DATA_IDS.applicationType.updatePlacement:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementStartEndDate:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementOneOffPayment:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementTransfer:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementTargetedCarePackage:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementCopStatusChange:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementCareAllowanceLevel:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementClientDetails:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementRespiteContinueToPay:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementMultipleElements:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementCancelPlacement:
      case REFERENCE_DATA_IDS.applicationType.updatePlacementRecallPlacement:

        this.navigate(['/', ROUTE_CONSTANTS.careAllowanceApplication.prefix, ROUTE_CONSTANTS.applicationDetailCreateUpdatePlacement.prefix, event.event.data.id]);
        break;
    }
  }

  private isPlacementOnHold(): boolean {
    return this.response.isPlacementOnHold;
  }

  private isPlacementJustReleasedFromHold(): boolean {
    return this.response.isPlacementJustReleasedFromHold;
  }

  private showViewAdjustmentsButton(): boolean {
    return this.response.showViewAdjustmentsButton;
  }

  private showHoldPlacementButton(): boolean {
    return this.response.showHoldPlacementButton;
  }

  private showReleasePlacementHoldButton(): boolean {
    return this.response.showReleasePlacementHoldButton;
  }

  private viewAdjustments() {
    const navExtras = this.getNavigationExtras();
    this.navigate([this.router.url, ROUTE_CONSTANTS.careAllowancePlacementAdjustment.listPrefix], false, navExtras);
  }

  private viewInvoices() {
    const navExtras = this.getNavigationExtras();  
    this.navigate([this.router.url, ROUTE_CONSTANTS.careAllowancePlacementInvoice.listPrefix], false, navExtras);
  }

  private holdPlacement() {
    this.activeDialog = POP_UP_CODES.careAllowancePlacement.holdPlacement;
    this.dialogService.showDialog(this.activeDialog);
  }

  private completeHoldPlacement(popupQuestion: CareAllowancePlacementHoldPopupQuestion) {
    const request = new CareAllowancePlacementHoldRequest(this.id, popupQuestion.placementHoldReasonTypeId, popupQuestion.placementHoldComments);
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.careAllowancePlacement.holdPlacement, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage();
          }
        }
        )
    );
  }

  private releasePlacementHold() {
    this.activeDialog = POP_UP_CODES.careAllowancePlacement.releasePlacementHold;
    this.dialogService.showDialog(this.activeDialog);
  }

  private completeReleasePlacementHold(popupQuestion: CareAllowancePlacementReleaseHoldPopupQuestion) {
    const request = new CareAllowancePlacementReleaseHoldRequest(this.id, popupQuestion.placementReleaseHoldReasonTypeId, popupQuestion.placementReleaseHoldComments);
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.careAllowancePlacement.releasePlacementHold, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage();
          }
        }
        )
    );
  }

  private navigateBackToHome(ignoreUnsavedChanges = false): void {
    const navExtras = this.getNavigationExtras();
    this.navigate([this.routePrefix], ignoreUnsavedChanges, navExtras);
  }

  private getNavigationExtras(): NavigationExtras {
    return {
      state: {
        placementFilterRequest: history.state.placementFilterRequest
      }
    };
  }

  private ceasePlacement() {
    this.activeDialog = POP_UP_CODES.careAllowancePlacement.ceasePlacement;
    this.dialogService.showDialog(this.activeDialog,
      {
        firstUnclosedRevisionStartDate: this.response.firstUnclosedRevisionStartDate,
        lastUnclosedRevisionEndDate: this.response.lastUnclosedRevisionEndDate
      });
  }

  dialogButton_ceasePlacement_Clicked(event: any) {
    this.activeDialog = POP_UP_CODES.careAllowancePlacement.ceasePlacementConfirmation;
    this.dialogService.showConfirm(this.activeDialog)
      .pipe(take(1))
      .subscribe(data => {
        if (data === DialogResult.ACCEPTED) {
          this.spinnerService.showSpinner();

          const request: CareAllowanceCeaseLocalAdoptionSaveRequest =
          {
            id: this.id,
            ceaseReasonId: event.ceasePlacementReason,
            comments: event.ceasePlacementComment,
            ceaseDate: getDateFromDDMMYYYYString(event.ceasePlacementDate)
          };
          this.registerSubscription(
            this.appDataService
              .post(API_ENDPOINTS.careAllowancePlacement.ceaseLocalAdoption, request)
              .pipe(
                take(1),
                this.errorService.errorHandler(),
                finalize(() => this.spinnerService.hideSpinner())
              )
              .subscribe(response => {
                if (response?.status === SAVE_RESULT_STATUS.success) {
                  this.reloadPage();
                }
              })
          );
        }
      });;
  }

  private showCeasePlacementButton(): boolean {
    return this.response.showCeasePlacementButton;
  }
}
